/* STATUS / AGENT NOTES (2026-10-17)
   Agent START Codex: Native-form lifecycle and accessible first-image gallery ownership. */
export function registerWingmateViewer({
  registry = globalThis.customElements,
  Element = globalThis.HTMLElement,
  loadModule = url => import(url),
  fetchMapping = async url => {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error('The reviewed product mapping could not load.');
    return response.json();
  },
} = {}) {
  if (!registry || !Element || registry.get('scooch-wingmate-viewer')) return;
  class ScoochWingmateViewer extends Element {
    connectedCallback() {
      if (this._connecting || this._controller) return;
      const button = this.querySelector('[data-scooch-wingmate-open]');
      const media = this.closest('product-media');
      const form = this.ownerDocument.getElementById(this.dataset.formId);
      let viewerURL, product, carousel;
      this._setGalleryPrimary(false);
      try {
        viewerURL = new URL(this.dataset.viewerUrl);
        if (viewerURL.protocol !== 'https:' || viewerURL.username || viewerURL.password || viewerURL.search || viewerURL.hash || !viewerURL.pathname.endsWith('/product.html')) throw new Error('A pinned hosted product viewer is required.');
        if (!button || !media || media.getAttribute('form-id') !== this.dataset.formId || !form?.elements?.namedItem('id')) throw new Error('The owning native product form is unavailable.');
        carousel = media.querySelector('flickity-carousel');
        if (!carousel) throw new Error('The owning native product gallery is unavailable.');
        product = JSON.parse(this.querySelector('[data-scooch-wingmate-product]').textContent);
      } catch {
        this.dataset.state = 'unavailable';
        return;
      }
      this._connecting = true;
      const generation = this._generation = (this._generation || 0) + 1;
      this._bindGallery(carousel, generation);
      this.dataset.state = 'loading';
      Promise.all([loadModule(this.dataset.modulesUrl), fetchMapping(this.dataset.mapUrl)])
        .then(([shared, mapping]) => {
          if (!this.isConnected || this._generation !== generation) return;
          this._controller = shared.mountWingmateProductSelection({
            button,
            readSelection: () => ({ product, variantId: form.elements.namedItem('id')?.value }),
            mapping,
            viewerURL: viewerURL.href,
            mountViewer: shared.mountWingmateProductViewer,
            eventTarget: form,
          });
          this.dataset.state = this._controller.selection.eligible ? 'ready' : 'unavailable';
        })
        .catch(() => {
          if (this._generation !== generation || !this.isConnected) return;
          button.hidden = true; button.disabled = true; button.setAttribute('aria-disabled', 'true');
          this.dataset.state = 'unavailable';
        })
        .finally(() => {
          if (this._generation === generation) this._connecting = false;
        });
    }
    _setGalleryPrimary(visible) {
      const value = visible ? 'true' : 'false';
      if (!visible && this.contains(this.ownerDocument.activeElement)) this.ownerDocument.activeElement.blur?.();
      if (this.dataset.galleryPrimary !== value) this.dataset.galleryPrimary = value;
      if (this.inert !== !visible) this.inert = !visible;
      const hidden = visible ? 'false' : 'true';
      if (this.getAttribute('aria-hidden') !== hidden) this.setAttribute('aria-hidden', hidden);
    }
    _bindGallery(carousel, generation) {
      let revision = 0;
      const current = () => this.isConnected && this._generation === generation;
      const setPrimary = (elements, selected) => {
        const firstImage = elements.find(element => element.getAttribute('data-media-type') === 'image');
        this._setGalleryPrimary(Boolean(firstImage && firstImage === selected));
      };
      const sync = async event => {
        if (!current() || (event && event.target !== carousel)) return;
        const request = ++revision;
        const pending = carousel.flickityInstance;
        const elements = Array.from(carousel.querySelectorAll('.product__media-item:not(.is-filtered)'));
        const selected = elements.find(element => element.classList.contains('is-selected'))
          || elements.find(element => element.classList.contains('is-initial-selected'))
          || (elements.length === 1 ? elements[0] : null);
        setPrimary(elements, selected);
        if (!pending) return;
        try {
          const instance = await pending;
          if (!current() || revision !== request || carousel.flickityInstance !== pending) return;
          const active = (instance?.cells || []).map(cell => cell.element)
            .filter(element => !element.classList.contains('is-filtered'));
          setPrimary(active, instance?.selectedElement);
        } catch {
          if (current() && revision === request && carousel.flickityInstance === pending) setPrimary([], null);
        }
      };
      const events = ['flickity:ready', 'flickity:slide-changed', 'flickity:slide-settled'];
      events.forEach(name => carousel.addEventListener(name, sync));
      this._disposeGallery = () => {
        revision++;
        events.forEach(name => carousel.removeEventListener(name, sync));
        this._setGalleryPrimary(false);
      };
      sync();
    }
    disconnectedCallback() {
      this._generation = (this._generation || 0) + 1;
      this._connecting = false;
      this._disposeGallery?.();
      this._disposeGallery = null;
      this._controller?.dispose();
      this._controller = null;
    }
  }
  registry.define('scooch-wingmate-viewer', ScoochWingmateViewer);
}
registerWingmateViewer();
/* Agent END Codex: Native-form lifecycle and accessible first-image gallery ownership. */
